import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Check, ChevronsUpDown, Download, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  usePendingDispatch,
  useReadyForHandover,
  useReadyForRelease,
  useDispatchHistoryPaged,
  useTrayReturnsHistoryPaged,
  useOutstandingTraysPaged,
  useConfirmHandover,
  useAcknowledgeReceipt,
  useInitiateDispatch,
  useCancelDispatch,
  useDispatchById,
} from "../hooks/useDispatch";
import {
  dispatchApi,
  generateDispatchIdempotencyKey,
  type DispatchRecordSummaryDto,
  type InitiateDispatchRequest,
} from "../services/dispatchApi";
import { TablePagination } from "@/shared/components/TablePagination";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useOrderById, useOrdersWithActiveReservations, usePagedDispatchableOrders } from "@/features/production/hooks/useOrders";
import { useGreenhouseStockEntries } from "@/features/production/hooks/useGreenhouseStock";
import type {
  Order,
  OrderItem,
  ReservedOrderSummary,
  ReservedOrderItem,
} from "@/features/production/services/ordersApi";
import type { GreenhouseStockEntryDto } from "@/features/production/services/sowingInstructionsApi";
import { normalizeError } from "@/shared/errors/normalizeError";
import { useDeliveryPlanDetail } from "../hooks/useDeliveryPlans";
import { CustomerCollectionsPanel } from "./CustomerCollectionsPanel";
import { PublishedPlanExecutionPanel } from "./PublishedPlanExecutionPanel";
import type { DispatchExecutionQueueLineDto } from "../services/deliveryPlansApi";
import {
  buildHandoverItemAllocations,
  clampHandoverQuantity,
  handoverUnitLabel,
  hasInvalidBedAllocations,
  isBedSelectionRequired,
  isHandoverBedLine,
  isPieceHandover,
  selectedBedQuantity,
} from "./dispatchHandover";

const KENYAN_COUNTIES = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo Marakwet", "Embu", "Garissa",
  "Homa Bay", "Isiolo", "Kajiado", "Kakamega", "Kericho", "Kiambu", "Kilifi",
  "Kirinyaga", "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia", "Lamu",
  "Machakos", "Makueni", "Mandera", "Marsabit", "Meru", "Migori", "Mombasa",
  "Murang'a", "Nairobi", "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua",
  "Nyeri", "Samburu", "Siaya", "Taita Taveta", "Tana River", "Tharaka Nithi",
  "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot",
];

const formatDate = (v?: string) => (v ? new Date(v).toLocaleDateString("en-KE") : "—");

/** Shared confirmation body: customer / order / trays + dispatched items. */
const dispatchSummaryBody = (rec: DispatchRecordSummaryDto) => (
  <div className="space-y-2">
    <div className="space-y-0.5">
      <p className="flex justify-between">
        <span className="text-muted-foreground">Customer</span>
        <span className="font-medium">{rec.customerName}</span>
      </p>
      <p className="flex justify-between">
        <span className="text-muted-foreground">Order</span>
        <span className="font-medium">{rec.orderNumber}</span>
      </p>
      <p className="flex justify-between">
        <span className="text-muted-foreground">Total trays</span>
        <span className="font-medium">{rec.totalTraysDispatched}</span>
      </p>
    </div>
    {rec.items?.length > 0 && (
      <div className="border-t pt-2">
        <p className="text-muted-foreground mb-1">Items</p>
        <ul className="space-y-0.5">
          {rec.items.map((it, i) => (
            <li key={i} className="flex justify-between gap-4">
              <span>
                {it.productName}
                {it.variety ? ` · ${it.variety}` : ""}
              </span>
              <span className="tabular-nums text-right">
                {it.quantityDispatched}
                {it.seedlingCount != null && (
                  <span className="text-muted-foreground"> · {it.seedlingCount.toLocaleString()} seedlings</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

function statusBadgeVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "HandedToDispatch": return "default";
    case "ReadyForRelease": return "secondary";
    case "Dispatched": return "default";
    case "Delivered": return "default";
    default: return "outline";
  }
}

function statusLabel(status: string) {
  switch (status) {
    case "Pending": return "Pending";
    case "HandedToDispatch": return "Handed to Dispatch";
    case "ReadyForRelease": return "Ready for Release";
    case "Dispatched": return "Dispatched";
    case "Delivered": return "Delivered";
    default: return status;
  }
}

// ── Initiate Dispatch Dialog ───────────────────────────────────────────────

const DISPATCHABLE_ORDERS_PAGE_SIZE = 20;

/** Legacy piece-priced walk-in display uses server-supplied sellable capacity. */
const trayFractionToSeedlings = (trays: number, capacity?: number | null): number | null => {
  if (!capacity) return null;
  const seedlings = Math.round(trays * capacity);
  return seedlings > 0 ? seedlings : null;
};

interface ItemRow {
  orderItemId: string;
  greenhouseStockEntryId: string;
  traysDispatched: string;
}

interface DispatchAssignmentPrefill {
  deliveryPlanId?: string;
  driverId?: string;
  driverName?: string;
  routeId?: string;
  routeName?: string;
  vehicleId?: string;
  vehicleName?: string;
  greenhouseId?: string;
  greenhouseName?: string;
  start?: string;
  end?: string;
}

const formatPrefillWindow = (prefill: DispatchAssignmentPrefill) => {
  if (!prefill.start || !prefill.end) return null;
  const start = new Date(prefill.start);
  const end = new Date(prefill.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return `${start.toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" })} - ${end.toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" })}`;
};

interface DispatchSubmissionContext {
  key: string;
  signature: string;
}
// Sub-component: loads available stock for one order item and renders a dropdown
function StockEntryPicker({
  orderItem,
  orderId,
  value,
  trays,
  pipelineReservedQty,
  isPending,
  isWalkIn,
  quantityLocked,
  onChange,
  onTraysChange,
}: {
  orderItem: OrderItem;
  orderId: string;
  value: string;
  trays: string;
  pipelineReservedQty?: number;
  isPending: boolean;
  isWalkIn?: boolean;
  quantityLocked?: boolean;
  onChange: (entryId: string, availableQty: number) => void;
  onTraysChange: (trays: string) => void;
}) {
  const stockQuery = useGreenhouseStockEntries();

  const normalize = (v?: string | null) => (v ?? "").trim().toLowerCase();
  const targetVariety = normalize(orderItem.variety);
  const entries: GreenhouseStockEntryDto[] = (stockQuery.data ?? []).filter((e) => {
    const varietyMatch = normalize(e.variety) === targetVariety;
    const dispatchable = e.availableQuantity + (e.reservedForOrderId === orderId ? e.reservedQuantity : 0);
    return varietyMatch && e.isActive && e.isReadyForDispatch && dispatchable > 0;
  });

  // Auto-select: prefer the batch reserved for this order, then the largest available
  const getDispatchableQty = (e: GreenhouseStockEntryDto) =>
    e.availableQuantity + (e.reservedForOrderId === orderId ? e.reservedQuantity : 0);

  const autoSelectedEntry = entries.find((e) => e.reservedForOrderId === orderId)
    ?? [...entries].sort((a, b) => getDispatchableQty(b) - getDispatchableQty(a))[0]
    ?? null;

  // Auto-select on mount or when entries load — dispatch manager doesn't need to pick manually
  useEffect(() => {
    if (!value && autoSelectedEntry) {
      onChange(autoSelectedEntry.id, getDispatchableQty(autoSelectedEntry));
    }
  }, [autoSelectedEntry?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const selected = entries.find((e) => e.id === value);
  // Stock is always tracked in TRAYS for tray-type entries, regardless of pricing mode.
  // Per-seedling pricing on a tray batch stores quantity as a tray fraction on OrderItem.
  const unitLabel = (orderItem.trayType != null) ? "trays" : (orderItem.pricingMode === 2 ? "pieces" : "trays");

  // For seedling-priced items, convert the stored tray fraction back to a seedling count for display.
  const orderedSeedlings = orderItem.pricingMode === 2 && orderItem.trayType != null
    ? trayFractionToSeedlings(orderItem.quantity, orderItem.sellablePerTray)
    : null;

  // Cap at remaining unfulfilled quantity to prevent over-dispatch
  const maxDispatch = selected
    ? Math.min(getDispatchableQty(selected), orderItem.quantityPending)
    : 0;

  /** Composed location string used in both the dropdown items and the compact trigger:
   *   one bay, many beds:   "Bay A · Bed 03, Bed 04"
   *   many bays, many beds: "Bay A · Bed 03 · Bay B · Bed 07"
   *   legacy (primary bed): "Bay A · Bed 03"
   * Returns null when no bay/bed info is recorded (germination-chamber stock). */
  const formatBayBedLabel = (e: GreenhouseStockEntryDto): string | null => {
    if (e.allBedLocations && e.allBedLocations.length > 0) {
      const byBay = new Map<string, string[]>();
      for (const loc of e.allBedLocations) {
        const beds = byBay.get(loc.bayName) ?? [];
        beds.push(loc.bedNumber);
        byBay.set(loc.bayName, beds);
      }
      return Array.from(byBay.entries())
        .map(([bay, beds]) => `${bay} · ${beds.join(", ")}`)
        .join(" · ");
    }
    if (e.bayName || e.bedNumber) {
      return [e.bayName, e.bedNumber].filter(Boolean).join(" · ");
    }
    return null;
  };

  return (
    <div className="border rounded p-3 space-y-3 bg-muted/30">
      <p className="text-sm font-medium">
        {[orderItem.cropType, orderItem.variety || orderItem.productName].filter(Boolean).join(" · ")}{" "}
        <span className="text-muted-foreground font-normal">
          {orderedSeedlings != null
            ? `(ordered: ${orderedSeedlings.toLocaleString()} seedlings)`
            : `(ordered: ${orderItem.quantity} ${unitLabel})`
          }
        </span>
      </p>
      {pipelineReservedQty != null && pipelineReservedQty > 0 && (
        <p className="text-xs text-amber-700">
          Reserved in pipeline (not ready yet): {pipelineReservedQty} {unitLabel}. Coordinate with Sales if this order needs full quantity now.
        </p>
      )}

      {/* Stock batch — auto-selected, shown as read-only info */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">
          Stock Batch (auto-assigned)
        </Label>
        {stockQuery.isLoading ? (
          <div className="h-9 rounded-md bg-muted animate-pulse" />
        ) : entries.length === 0 ? (
          <p className="text-xs text-destructive">No stock available for this variety</p>
        ) : selected ? (
          <div className="text-xs p-2 rounded border bg-muted/50">
            <span className="font-medium">
              {[selected.cropTypeName, selected.variety].filter(Boolean).join(" · ")}
              {selected.trayType ? ` (${selected.trayType})` : ""}
            </span>
            {(() => {
              const bayBed = formatBayBedLabel(selected);
              const loc = [selected.greenhouseName, bayBed]
                .filter((p): p is string => !!p && p.length > 0)
                .join(" · ");
              return loc ? <span className="text-muted-foreground"> — {loc}</span> : null;
            })()}
            <span className="text-muted-foreground ml-2">
              · {getDispatchableQty(selected)} {unitLabel} dispatchable
            </span>
            {selected.reservedForOrderId === orderId && selected.reservedQuantity > 0 && (
              <span className="text-green-600 ml-1">(reserved for this order)</span>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Loading batch...</p>
        )}
      </div>

      {/* Quantity count — shown only once a batch is selected */}
      {selected && (
        <div className="space-y-1">
          <Label className="text-xs">
            {unitLabel === "pieces" ? "Pieces to Dispatch" : "Trays to Dispatch"} <span className="text-destructive">*</span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={maxDispatch}
              value={trays}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || Number(v) <= maxDispatch) {
                  onTraysChange(v);
                } else {
                  onTraysChange(maxDispatch.toString());
                }
              }}
              disabled={isPending || isWalkIn || quantityLocked}
              readOnly={isWalkIn || quantityLocked}
              className="w-32"
              title={quantityLocked ? "Quantity locked to the Transport Manager plan" : isWalkIn ? "Walk-in order — quantity fixed at point of sale" : undefined}
            />
            <span className="text-xs text-muted-foreground">
              {(() => {
                const dispQty = getDispatchableQty(selected);
                const cappedByOrder = dispQty > orderItem.quantityPending;
                const dispSeedlings = trayFractionToSeedlings(maxDispatch, orderItem.trayType);
                if (orderedSeedlings != null && dispSeedlings != null) {
                  return `${dispSeedlings.toLocaleString()} seedlings (${maxDispatch} trays) remaining`;
                }
                return cappedByOrder
                  ? `of ${maxDispatch} ${unitLabel} remaining (${dispQty} in stock)`
                  : `of ${maxDispatch} ${unitLabel} dispatchable`;
              })()}
            </span>
          </div>
          {/* For seedling-priced items, show the tray↔seedling breakdown so the value is unambiguous */}
          {orderedSeedlings != null && (() => {
            const sellable = orderItem.sellablePerTray;
            const enteredSeedlings = trayFractionToSeedlings(Number(trays), orderItem.sellablePerTray);
            return (
              <p className="text-xs text-muted-foreground">
                Quantity is in <strong>trays</strong>
                {sellable != null && ` (1 tray = ${sellable} sellable seedlings)`}
                {enteredSeedlings != null && ` — ${trays} trays = ${enteredSeedlings.toLocaleString()} seedlings`}
              </p>
            );
          })()}
          {Number(trays) > getDispatchableQty(selected) && (
            <p className="text-xs text-destructive">
              Exceeds dispatchable stock ({getDispatchableQty(selected)} {unitLabel})
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function InitiateDispatchDialog({
  open,
  onOpenChange,
  onSuccess,
  prefill,
  preSelectedOrderId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => void;
  prefill?: DispatchAssignmentPrefill | null;
  preSelectedOrderId?: string | null;
}) {
  const notify = useNotification();
  const confirm = useConfirm();
  const initiate = useInitiateDispatch();
  const reservedOrdersQuery = useOrdersWithActiveReservations(open);
  const sourcePlanQuery = useDeliveryPlanDetail(prefill?.deliveryPlanId ?? "");

  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [orderPopoverOpen, setOrderPopoverOpen] = useState(false);
  const [orderSearchInput, setOrderSearchInput] = useState("");
  const debouncedOrderSearch = useDebounce(orderSearchInput, 300);
  const [orderPage, setOrderPage] = useState(1);
  const [dispatchableOrders, setDispatchableOrders] = useState<Order[]>([]);
  const [notes, setNotes] = useState("");
  const [itemRows, setItemRows] = useState<ItemRow[]>([]);
  const submissionContextRef = useRef<DispatchSubmissionContext | null>(null);
  const prefillWindow = formatPrefillWindow(prefill ?? {});

  const clearSubmissionContext = () => {
    submissionContextRef.current = null;
  };

  const toInitiatePayload = (): InitiateDispatchRequest => ({
    orderId: selectedOrderId,
    fulfilmentMode: "Delivery",
    deliveryPlanId: prefill?.deliveryPlanId,
    notes: notes || undefined,
    items: itemRows.map((r) => ({
      orderItemId: r.orderItemId,
      deliveryPlanStopItemId: sourcePlan?.stops
        .find((stop) => stop.orderId === selectedOrderId)?.items
        .find((item) => item.orderItemId === r.orderItemId)?.id,
      greenhouseStockEntryId: r.greenhouseStockEntryId,
      quantityDispatched: Number(r.traysDispatched),
    })),
  });

  const buildSubmissionSignature = (payload: InitiateDispatchRequest): string =>
    JSON.stringify({
      orderId: payload.orderId,
      notes: payload.notes ?? "",
      items: payload.items
        .map((item) => ({
          orderItemId: item.orderItemId,
          greenhouseStockEntryId: item.greenhouseStockEntryId,
          quantityDispatched: item.quantityDispatched,
        }))
        .sort((a, b) => {
          const left = `${a.orderItemId}:${a.greenhouseStockEntryId}`;
          const right = `${b.orderItemId}:${b.greenhouseStockEntryId}`;
          return left.localeCompare(right);
        }),
    });

  const resolveSubmissionIdempotencyKey = (payload: InitiateDispatchRequest): string => {
    const signature = buildSubmissionSignature(payload);
    if (submissionContextRef.current?.signature === signature) {
      return submissionContextRef.current.key;
    }

    const key = generateDispatchIdempotencyKey();
    submissionContextRef.current = { key, signature };
    return key;
  };

  const dispatchableOrdersQuery = usePagedDispatchableOrders({
    search: debouncedOrderSearch.trim() || undefined,
    pageNumber: orderPage,
    pageSize: DISPATCHABLE_ORDERS_PAGE_SIZE,
    enabled: open,
  });

  const selectedOrderQuery = useOrderById(selectedOrderId);

  useEffect(() => {
    if (!open) {
      setSelectedOrderId("");
      setOrderPopoverOpen(false);
      setOrderSearchInput("");
      setOrderPage(1);
      setDispatchableOrders([]);
      setItemRows([]);
      setNotes("");
      clearSubmissionContext();
    }
  }, [open]);

  // Pre-select order when opened from the grid
  useEffect(() => {
    if (open && preSelectedOrderId && !selectedOrderId) {
      setSelectedOrderId(preSelectedOrderId);
    }
  }, [open, preSelectedOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOrderPage(1);
    setDispatchableOrders([]);
  }, [debouncedOrderSearch]);

  useEffect(() => {
    const items = dispatchableOrdersQuery.data?.items;
    if (!items) return;
    if (dispatchableOrdersQuery.isPlaceholderData && orderPage === 1 && dispatchableOrders.length === 0) {
      return;
    }

    setDispatchableOrders((previous) => {
      if (orderPage <= 1) return items;

      const merged = new Map(previous.map((order) => [order.id, order]));
      for (const order of items) {
        merged.set(order.id, order);
      }
      return Array.from(merged.values());
    });
  }, [dispatchableOrdersQuery.data, dispatchableOrdersQuery.isPlaceholderData, dispatchableOrders.length, orderPage]);

  const hasMoreDispatchableOrders =
    (dispatchableOrdersQuery.data?.currentPage ?? 1) < (dispatchableOrdersQuery.data?.totalPages ?? 1);

  const loadingOrders = dispatchableOrdersQuery.isLoading && dispatchableOrders.length === 0;
  const loadingMoreOrders = dispatchableOrdersQuery.isFetching && orderPage > 1;
  const isPlannedDelivery = !!prefill?.deliveryPlanId;
  const sourcePlan = sourcePlanQuery.data;
  const visibleDispatchableOrders = isPlannedDelivery
    ? (sourcePlan
      ? dispatchableOrders.filter((order) => sourcePlan.stops.some((stop) => stop.orderId === order.id))
      : [])
    : dispatchableOrders;

  const selectedOrder: Order | undefined =
    selectedOrderQuery.data ?? dispatchableOrders.find((o) => o.id === selectedOrderId);

  const selectedReservedOrder: ReservedOrderSummary | undefined =
    (reservedOrdersQuery.data ?? []).find((o) => o.orderId === selectedOrderId);

  const pipelineReservedQuantity = selectedReservedOrder?.notReadyQuantity ?? 0;
  const plannedStop = sourcePlan?.stops.find((stop) => stop.orderId === selectedOrderId);
  const plannedQuantityByItem = new Map(
    (plannedStop?.items ?? []).map((item) => [item.orderItemId, item.executableQuantity ?? item.plannedQuantity])
  );

  const getItemPipelineReservedQty = (itemId: string): number => {
    const reservedItem: ReservedOrderItem | undefined =
      selectedReservedOrder?.items.find((item) => item.orderItemId === itemId);
    return (reservedItem?.reservedInstructions ?? [])
      .filter((ins) => !ins.isReadyForDispatch)
      .reduce((sum, ins) => sum + ins.reservedQuantity, 0);
  };

  const handleOrderChange = (orderId: string) => {
    setSelectedOrderId(orderId);
    const order = dispatchableOrders.find((o) => o.id === orderId);
    const targetStop = sourcePlan?.stops.find((stop) => stop.orderId === orderId);
    const targetQuantities = new Map(
      (targetStop?.items ?? []).map((item) => [item.orderItemId, item.executableQuantity ?? item.plannedQuantity])
    );
    setOrderSearchInput("");
    setOrderPage(1);
    setItemRows(
      order?.orderItems
        .filter((item: OrderItem) => !isPlannedDelivery || targetQuantities.has(item.id))
        .map((item: OrderItem) => ({
          orderItemId: item.id,
          greenhouseStockEntryId: item.greenhouseStockEntryId ?? "",
          traysDispatched: String(targetQuantities.get(item.id) ?? item.quantity),
        })) ?? []
    );
  };

  useEffect(() => {
    if (!selectedOrderId || !selectedOrder || itemRows.length > 0 || selectedOrder.orderItems.length === 0) {
      return;
    }

    const initialPlannedQuantities = new Map(
      (plannedStop?.items ?? []).map((item) => [item.orderItemId, item.executableQuantity ?? item.plannedQuantity])
    );
    setItemRows(
      selectedOrder.orderItems
        .filter((item: OrderItem) => !isPlannedDelivery || initialPlannedQuantities.has(item.id))
        .map((item: OrderItem) => ({
          orderItemId: item.id,
          greenhouseStockEntryId: item.greenhouseStockEntryId ?? "",
          traysDispatched: String(initialPlannedQuantities.get(item.id) ?? item.quantity),
        }))
    );
  }, [selectedOrderId, selectedOrder, itemRows.length, isPlannedDelivery, plannedStop?.id]);

  const updateRow = (idx: number, field: keyof ItemRow, value: string) =>
    setItemRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const resolveInitiateErrorMessage = (err: unknown): string => {
    const env = normalizeError(err);
    const code = env.code.toLowerCase();

    if (code === "idempotency-key_required" || code === "idempotency-key_too_long") {
      console.error("Dispatch initiation failed due to idempotency header validation", {
        traceId: env.traceId,
        code: env.code,
        httpStatus: env.httpStatus,
      });
      return "We could not initiate the dispatch. Please try again.";
    }

    if (
      code === "idempotency_payload_mismatch" ||
      code === "dispatch_plan_line_already_owned"
    ) {
      return `${env.userMessage}${env.nextStep ? ` ${env.nextStep}` : ""}`;
    }

    return env.userMessage || "Failed to initiate dispatch";
  };

  const handleSubmit = async () => {
    if (!selectedOrderId) { notify.error("Select an order"); return; }
    if (itemRows.some((r) => !r.greenhouseStockEntryId)) {
      notify.error("Select a stock batch for every item");
      return;
    }
    if (itemRows.some((r) => !r.traysDispatched || Number(r.traysDispatched) <= 0)) {
      notify.error("Enter a valid dispatch quantity for every item");
      return;
    }

    const payload = toInitiatePayload();
    const idempotencyKey = resolveSubmissionIdempotencyKey(payload);

    const confirmed = await confirm({
      title: "Initiate dispatch?",
      description: selectedOrder
        ? `${selectedOrder.customerName} — ${selectedOrder.orderNumber} (Delivery)`
        : "A delivery dispatch record will be created.",
      confirmLabel: "Initiate dispatch",
      body: (
        <div className="space-y-2">
          <div className="space-y-0.5">
            <p className="flex justify-between">
              <span className="text-muted-foreground">Customer</span>
              <span className="font-medium">{selectedOrder?.customerName}</span>
            </p>
            <p className="flex justify-between">
              <span className="text-muted-foreground">Order</span>
              <span className="font-medium">{selectedOrder?.orderNumber}</span>
            </p>
            <p className="flex justify-between">
              <span className="text-muted-foreground">Fulfilment</span>
              <span className="font-medium">Delivery (selected by Sales)</span>
            </p>
          </div>
          <div className="border-t pt-2">
            <p className="text-muted-foreground mb-1">Dispatching</p>
            <ul className="space-y-0.5">
              {itemRows.map((r, i) => {
                const oi = selectedOrder?.orderItems?.find((x) => x.id === r.orderItemId);
                const seedlings = oi
                  ? trayFractionToSeedlings(Number(r.traysDispatched), oi.trayType)
                  : null;
                return (
                  <li key={i} className="flex justify-between gap-4">
                    <span>
                      {oi
                        ? [oi.cropType, oi.variety || oi.productName].filter(Boolean).join(" · ")
                        : "Item"}
                    </span>
                    <span className="tabular-nums text-right">
                      {r.traysDispatched} trays
                      {seedlings != null && (
                        <span className="text-muted-foreground"> · {seedlings.toLocaleString()} seedlings</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ),
      onConfirm: () =>
        initiate
          .mutateAsync({ data: payload, idempotencyKey })
          .then(() => {
            notify.success("Dispatch record created");
            onOpenChange(false);
            setSelectedOrderId("");
            setItemRows([]);
            setNotes("");
            setOrderSearchInput("");
            setOrderPage(1);
            setDispatchableOrders([]);
            clearSubmissionContext();
            onSuccess();
          })
          .catch((err: unknown) => {
            notify.error(resolveInitiateErrorMessage(err));
            throw err;
          }),
    });

    if (!confirmed) {
      clearSubmissionContext();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && initiate.isPending) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Initiate Dispatch</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {prefill && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              <p className="font-medium">Calendar assignment context detected</p>
              <div className="mt-1 space-y-0.5 text-xs text-blue-800">
                {prefill.driverName && <p>Driver: {prefill.driverName}</p>}
                {prefill.routeName && <p>Route: {prefill.routeName}</p>}
                {prefill.vehicleName && <p>Vehicle: {prefill.vehicleName}</p>}
                {prefill.greenhouseName && <p>Greenhouse: {prefill.greenhouseName}</p>}
                {prefillWindow && <p>Window: {prefillWindow}</p>}
              </div>
              <p className="mt-1 text-xs text-blue-700">
                Choose the order to dispatch for this slot. Driver assignment happens when finalizing dispatch details.
              </p>
            </div>
          )}

          <div className="space-y-1">
            <Label>Order <span className="text-destructive">*</span></Label>
            <Popover
              open={orderPopoverOpen}
              onOpenChange={(nextOpen) => {
                setOrderPopoverOpen(nextOpen);
                if (!nextOpen) {
                  setOrderSearchInput("");
                  setOrderPage(1);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={orderPopoverOpen}
                  className="w-full justify-between font-normal"
                  disabled={initiate.isPending}
                >
                  {selectedOrderId
                    ? selectedOrder
                      ? `${selectedOrder.orderNumber} — ${selectedOrder.customerName}`
                      : "Loading order…"
                    : loadingOrders
                      ? "Loading orders…"
                      : "Search by order number or customer…"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Type to search…"
                    value={orderSearchInput}
                    onValueChange={setOrderSearchInput}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {dispatchableOrdersQuery.isFetching && dispatchableOrders.length === 0
                        ? "Loading orders…"
                        : "No orders ready for dispatch"}
                    </CommandEmpty>
                    <CommandGroup>
                      {visibleDispatchableOrders.map((o) => (
                        <CommandItem
                          key={o.id}
                          value={`${o.orderNumber} ${o.customerName}`}
                          onSelect={() => {
                            handleOrderChange(o.id);
                            setOrderPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedOrderId === o.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {o.orderNumber} — {o.customerName}
                        </CommandItem>
                      ))}
                    </CommandGroup>

                    {hasMoreDispatchableOrders && (
                      <div className="border-t p-2">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 w-full"
                          onClick={() => setOrderPage((currentPage) => currentPage + 1)}
                          disabled={loadingMoreOrders || initiate.isPending}
                        >
                          {loadingMoreOrders ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Loading more…
                            </>
                          ) : (
                            "Load more"
                          )}
                        </Button>
                      </div>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            <span className="text-muted-foreground">Fulfilment method: </span>
            <span className="font-medium">Delivery</span>
            <p className="mt-1 text-xs text-muted-foreground">Selected by Sales and inherited from the published Transport Manager plan.</p>
          </div>

          {selectedReservedOrder && pipelineReservedQuantity > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1">
              <p className="text-sm font-medium text-amber-900">Pipeline reservation notice</p>
              <p className="text-xs text-amber-800">
                {pipelineReservedQuantity} reserved units for this order are still in pipeline (not ready for dispatch).
                Coordinate with Sales before finalizing this dispatch.
              </p>
              <p className="text-xs text-amber-700">
                Ready reserved: {selectedReservedOrder.readyQuantity} · Total reserved: {selectedReservedOrder.totalReservedQuantity}
              </p>
            </div>
          )}

          {isPlannedDelivery && sourcePlan && (
            <div className="rounded-md border border-sky-200 bg-sky-50 p-3 space-y-2">
              <p className="text-sm font-medium text-sky-900">Transport Manager plan (quantity locked)</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary">Route: {sourcePlan.routeName}</Badge>
                <Badge variant="outline">Driver: {sourcePlan.driverName}</Badge>
                <Badge variant="outline">Vehicle: {sourcePlan.vehicleRegistrationNumber}</Badge>
              </div>
              {selectedOrder && plannedStop && plannedStop.items.map((line) => {
                const orderItem = selectedOrder.orderItems.find((item) => item.id === line.orderItemId);
                if (!orderItem) return null;
                const previouslyDelivered = Math.max(0, orderItem.quantity - orderItem.quantityPending);
                return (
                  <p key={line.id} className="text-xs text-sky-900">
                    {line.productName}{line.variety ? ` · ${line.variety}` : ""}: Ordered {orderItem.quantity} · Previously Delivered {previouslyDelivered} · Planned {line.plannedQuantity} · Remaining {Math.max(0, orderItem.quantityPending - line.plannedQuantity)}
                  </p>
                );
              })}
            </div>
          )}

          {selectedOrder && itemRows.length > 0 && (
            <div className="space-y-2">
              <Label>Items</Label>
              {itemRows.map((row, idx) => {
                const orderItem = selectedOrder.orderItems.find(
                  (i: OrderItem) => i.id === row.orderItemId
                );
                if (!orderItem) return null;
                return (
                  <StockEntryPicker
                    key={row.orderItemId}
                    orderItem={orderItem}
                    orderId={selectedOrder.id}
                    value={row.greenhouseStockEntryId}
                    trays={row.traysDispatched}
                    pipelineReservedQty={getItemPipelineReservedQty(row.orderItemId)}
                    isPending={initiate.isPending}
                    isWalkIn={selectedOrder.isWalkIn}
                    quantityLocked={isPlannedDelivery}
                    onChange={(entryId, availQty) => {
                      updateRow(idx, "greenhouseStockEntryId", entryId);
                      // Auto-fill trays with remaining qty capped to available stock
                      const capped = isPlannedDelivery
                        ? (plannedQuantityByItem.get(orderItem.id) ?? 0)
                        : Math.min(orderItem.quantityPending, availQty);
                      updateRow(idx, "traysDispatched", capped.toString());
                    }}
                    onTraysChange={(v) => updateRow(idx, "traysDispatched", v)}
                  />
                );
              })}
            </div>
          )}

          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              placeholder="Optional notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={initiate.isPending}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={initiate.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={initiate.isPending || !selectedOrderId}
          >
            {initiate.isPending ? "Initiating..." : "Initiate Dispatch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Growing Supervisor Queue ───────────────────────────────────────────────

function GrowingSupervisorQueue() {
  const notification = useNotification();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput.trim(), 400);
  const { data, isLoading, isFetching, refetch } = usePendingDispatch(page, pageSize, search || undefined);
  const records = data?.items ?? [];
  const confirmHandover = useConfirmHandover();
  const cancelDispatch = useCancelDispatch();

  const [selected, setSelected] = useState<DispatchRecordSummaryDto | null>(null);
  const { data: selectedDetail, isLoading: loadingSelectedDetail } = useDispatchById(selected?.id);
  const [notes, setNotes] = useState("");
  const [bedQuantities, setBedQuantities] = useState<Record<string, number>>({});
  const [open, setOpen] = useState(false);

  const [cancelTarget, setCancelTarget] = useState<DispatchRecordSummaryDto | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);

  const handleSearch = (value: string) => {
    setSearchInput(value);
    setPage(1);
  };

  const handleOpen = (rec: DispatchRecordSummaryDto) => {
    setSelected(rec);
    setNotes("");
    setBedQuantities({});
    setOpen(true);
  };

  const handleConfirm = () => {
    if (!selected || !selectedDetail || hasInvalidBedAllocations(selectedDetail.items, bedQuantities)) return;
    const rec = selected;
    confirm({
      title: "Confirm handover?",
      description: `Dispatch ${rec.dispatchNumber} will be handed over for delivery.`,
      confirmLabel: "Confirm handover",
      body: dispatchSummaryBody(rec),
      onConfirm: async () => {
        try {
          await confirmHandover.mutateAsync({
            id: rec.id,
            data: {
              notes: notes || undefined,
              itemAllocations: buildHandoverItemAllocations(selectedDetail.items, bedQuantities),
            },
          });
          notification.success("Handover confirmed");
          setOpen(false);
          refetch();
        } catch (err) {
          const env = normalizeError(err);
          notification.error(env, { severity: "warning", durationMs: 8_000 });
          throw err;
        }
      },
    });
  };

  const handleCancelOpen = (rec: DispatchRecordSummaryDto) => {
    setCancelTarget(rec);
    setCancelReason("");
    setCancelOpen(true);
  };

  const handleCancelSubmit = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      notification.error("A cancellation reason is required");
      return;
    }
    try {
      await cancelDispatch.mutateAsync({
        id: cancelTarget.id,
        data: { reason: cancelReason.trim() },
      });
      notification.success(`Dispatch ${cancelTarget.dispatchNumber} cancelled`);
      setCancelOpen(false);
      refetch();
    } catch (err: unknown) {
      notification.error(normalizeError(err).userMessage || "Failed to cancel dispatch");
    }
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-base">Pending Handover to Dispatch</CardTitle>
            <div className="relative w-72">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search dispatch, order, customer, route…"
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                className="h-8 pl-8 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {records.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {search ? "No pending handovers match that search." : "No records pending handover."}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dispatch No.</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.dispatchNumber}</TableCell>
                    <TableCell className="font-mono text-xs">{r.orderNumber}</TableCell>
                    <TableCell>{r.customerName}</TableCell>
                    <TableCell>{r.fulfilmentMode}</TableCell>
                    <TableCell>
                      {r.items.length === 0 ? (
                        <span className="text-muted-foreground text-xs">{r.totalTraysDispatched} trays</span>
                      ) : (
                        <div className="space-y-0.5">
                          {r.items.map((item, idx) => (
                            <div key={idx} className="text-xs">
                              <span>{item.productName}</span>
                              {item.variety && <span className="text-muted-foreground"> · {item.variety}</span>}
                              {item.seedlingCount != null && (
                                <span className="text-muted-foreground"> ({item.seedlingCount.toLocaleString()} seedlings)</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(r.createdAt)}</TableCell>
                    <TableCell className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/dispatch/${r.id}`)}>View</Button>
                      <PermissionGate permissions={["dispatch.confirm_handover"]}>
                        <Button size="sm" onClick={() => handleOpen(r)}>Confirm Handover</Button>
                      </PermissionGate>
                      <PermissionGate permissions={["dispatch.cancel"]}>
                        <Button size="sm" variant="destructive" onClick={() => handleCancelOpen(r)}>Cancel</Button>
                      </PermissionGate>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {(data?.totalCount ?? 0) > 0 && (
            <TablePagination
              pageNumber={data?.pageNumber ?? page}
              pageSize={data?.pageSize ?? pageSize}
              totalCount={data?.totalCount ?? 0}
              totalPages={data?.totalPages ?? 0}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              isFetching={isFetching}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm Handover — {selected?.dispatchNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select the exact greenhouse beds the stock is being removed from, then confirm the physical handover to Dispatch.
            </p>
            {loadingSelectedDetail ? (
              <p className="text-sm text-muted-foreground">Loading stock locations...</p>
            ) : (
              (selectedDetail?.items ?? [])
                .map((item) => {
                  const unitLabel = handoverUnitLabel(item);
                  if (!isHandoverBedLine(item)) {
                    return (
                      <div key={item.id} className="rounded-md border p-3 space-y-1">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium">{item.cropTypeName}{item.variety ? ` / ${item.variety}` : ""}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.traysDispatched} {item.unitName ?? item.unit} — sold per piece, no greenhouse bed required.
                            </p>
                          </div>
                          <Badge variant="secondary">No bed selection</Badge>
                        </div>
                      </div>
                    );
                  }
                  const selectedTotal = selectedBedQuantity(item, bedQuantities);
                  return (
                    <div key={item.id} className="rounded-md border p-3 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-medium">{item.cropTypeName}{item.variety ? ` / ${item.variety}` : ""}</p>
                          <p className="text-xs text-muted-foreground">Choose {item.bedAllocationRequiredQuantity} fresh greenhouse {unitLabel} from the recorded beds.</p>
                          {item.handoverBedOptions.some((bed) => bed.isSystemReservedLocation) && (
                            <p className="mt-1 text-xs font-medium text-emerald-700">
                              The system reserved stock for this order at the location marked below.
                            </p>
                          )}
                        </div>
                        <Badge variant={selectedTotal === item.bedAllocationRequiredQuantity ? "default" : "secondary"}>
                          {selectedTotal} / {item.bedAllocationRequiredQuantity} {unitLabel} selected
                        </Badge>
                      </div>
                      {item.bedAllocationRequiredQuantity === 0 ? (
                        <p className="text-sm text-muted-foreground">This line is fulfilled from returned dispatch custody; no greenhouse bed deduction is required.</p>
                      ) : !isBedSelectionRequired(item) ? (
                        <p className="text-sm text-amber-700">Recorded greenhouse beds do not cover this quantity for this stock batch. Handover can proceed without bed selection.</p>
                      ) : (
                        <div className="space-y-2">
                          {item.handoverBedOptions.map((bed) => {
                            const key = `${item.id}:${bed.greenhouseStockEntryBedId}`;
                            return (
                              <div key={key} className="grid grid-cols-[1fr_120px] items-center gap-3">
                                <div className="text-sm">
                                  <span className="font-medium">{bed.greenhouseName}</span>{" "}
                                  {bed.isSystemReservedLocation && <Badge variant="secondary">System reserved</Badge>}
                                  <span className="text-muted-foreground"> / {bed.bayName} / {bed.bedNumber} ({bed.availableTrays} {unitLabel})</span>
                                </div>
                                <Input
                                  type="number"
                                  min={0}
                                  max={bed.availableTrays}
                                  step={isPieceHandover(item) ? 1 : "any"}
                                  value={bedQuantities[key] ?? ""}
                                  placeholder={isPieceHandover(item) ? "Pieces" : "Trays"}
                                  aria-label={`${bed.bayName} / ${bed.bedNumber} quantity (${unitLabel})`}
                                  onChange={(event) => {
                                    const quantity = clampHandoverQuantity(event.target.value, bed.availableTrays, item);
                                    setBedQuantities((current) => ({ ...current, [key]: quantity }));
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any handover remarks…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={confirmHandover.isPending || loadingSelectedDetail || !selectedDetail ||
                hasInvalidBedAllocations(selectedDetail.items, bedQuantities)}
            >
              {confirmHandover.isPending ? "Confirming…" : "Confirm Handover"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Dispatch — {cancelTarget?.dispatchNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Cancelling will remove this Pending dispatch for <strong>{cancelTarget?.customerName}</strong>. The order remains dispatchable; you can initiate a new dispatch afterwards.
            </p>
            <div className="space-y-1">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why is this dispatch being cancelled?"
                disabled={cancelDispatch.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelDispatch.isPending}>
              Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubmit}
              disabled={cancelDispatch.isPending || !cancelReason.trim()}
            >
              {cancelDispatch.isPending ? "Cancelling…" : "Cancel Dispatch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Dispatch Manager Workspace ─────────────────────────────────────────────

function DispatchManagerWorkspace() {
  const notification = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ackPage, setAckPage] = useState(1);
  const [ackPageSize, setAckPageSize] = useState(25);
  const [releasePage, setReleasePage] = useState(1);
  const [releasePageSize, setReleasePageSize] = useState(25);

  const { data: awaitingAckData, isLoading: loadingAck, isFetching: fetchingAck, refetch: refetchAck } = useReadyForHandover(ackPage, ackPageSize);
  const { data: readyForReleaseData, isLoading: loadingRelease, isFetching: fetchingRelease, refetch: refetchRelease } = useReadyForRelease(releasePage, releasePageSize);
  const awaitingAck = awaitingAckData?.items ?? [];
  const readyForRelease = readyForReleaseData?.items ?? [];
  const acknowledgeReceipt = useAcknowledgeReceipt();
  const confirm = useConfirm();

  const managerTab = searchParams.get("managerTab") === "release" ? "release" : "awaiting";

  const handleManagerTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "awaiting") {
      next.delete("managerTab");
    } else {
      next.set("managerTab", value);
    }
    setSearchParams(next, { replace: true });
  };

  const [selected, setSelected] = useState<DispatchRecordSummaryDto | null>(null);
  const [notes, setNotes] = useState("");
  const [ackOpen, setAckOpen] = useState(false);

  const handleAckOpen = (rec: DispatchRecordSummaryDto) => {
    setSelected(rec);
    setNotes("");
    setAckOpen(true);
  };

  const handleAcknowledge = () => {
    if (!selected) return;
    const rec = selected;
    confirm({
      title: "Acknowledge receipt?",
      description: `Receipt of dispatch ${rec.dispatchNumber} will be acknowledged.`,
      confirmLabel: "Acknowledge",
      body: dispatchSummaryBody(rec),
      onConfirm: async () => {
        try {
          await acknowledgeReceipt.mutateAsync({ id: rec.id, data: { notes: notes || undefined } });
          notification.success("Receipt acknowledged");
          setAckOpen(false);
          refetchAck();
          refetchRelease();
        } catch (err) {
          const env = normalizeError(err);
          notification.error(env, { severity: "warning", durationMs: 8_000 });
          throw err;
        }
      },
    });
  };

  const renderDispatchTable = ({
    records,
    loading,
    showAck,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    isFetching,
    onPageChange,
    onPageSizeChange,
  }: {
    records: DispatchRecordSummaryDto[];
    loading: boolean;
    showAck: boolean;
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    isFetching: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
  }) => {
    if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
    return (
      <>
        {records.length === 0 ? (
          <p className="text-sm text-muted-foreground">No records.</p>
        ) : (
          <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Dispatch No.</TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Assignment</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono text-xs">{r.dispatchNumber}</TableCell>
              <TableCell className="font-mono text-xs">{r.orderNumber}</TableCell>
              <TableCell>{r.customerName}</TableCell>
              <TableCell className="whitespace-normal min-w-[150px] max-w-[220px]">
                {r.fulfilmentMode === "Delivery" ? (
                  <div className="space-y-1 text-xs">
                    <Badge variant={r.assignmentInheritedFromPlan ? "default" : "outline"}>
                      {r.assignmentInheritedFromPlan ? "Planned delivery" : "Manual delivery"}
                    </Badge>
                    {r.deliveryPlanName && (
                      <p className="font-medium">{r.deliveryPlanName}</p>
                    )}
                    <p className="text-muted-foreground">
                      {r.driverName || "Driver pending"} · {r.vehicleNumber || "Vehicle pending"}
                    </p>
                  </div>
                ) : (
                  <Badge variant="secondary">Customer collection</Badge>
                )}
              </TableCell>
              <TableCell className="whitespace-normal min-w-[220px] max-w-[340px]">
                {r.items.length === 0 ? (
                  <span className="text-muted-foreground text-xs">{r.totalTraysDispatched} trays</span>
                ) : (
                  <div className="space-y-0.5">
                    {r.items.map((item, idx) => (
                      <div key={idx} className="text-xs">
                        <span>{item.productName}</span>
                        {item.variety && <span className="text-muted-foreground"> · {item.variety}</span>}
                        {item.seedlingCount != null && (
                          <span className="text-muted-foreground"> ({item.seedlingCount.toLocaleString()} seedlings)</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={statusBadgeVariant(r.status)}>{statusLabel(r.status)}</Badge>
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/dispatch/${r.id}`)}>
                    View
                  </Button>
                  {["Dispatched", "InTransit", "Delivered"].includes(r.status) && (
                    <PermissionGate permissions={["dispatch.print_delivery_note"]}>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        title="Download Delivery Note"
                        onClick={() => dispatchApi.downloadDeliveryNotePdf(r.id)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                  )}
                  {showAck && (
                    <PermissionGate permissions={["dispatch.acknowledge_receipt"]}>
                      <Button size="sm" onClick={() => handleAckOpen(r)}>Acknowledge</Button>
                    </PermissionGate>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
          </Table>
        )}
        {totalCount > 0 && (
          <TablePagination
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            isFetching={isFetching}
          />
        )}
      </>
    );
  };

  return (
    <>
      <Tabs value={managerTab} onValueChange={handleManagerTabChange}>
        <TabsList>
          <TabsTrigger value="awaiting">
            Awaiting Acknowledgement
            {(awaitingAckData?.totalCount ?? 0) > 0 && (
              <Badge variant="secondary" className="ml-2">{awaitingAckData?.totalCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="release">
            Ready for Release
            {(readyForReleaseData?.totalCount ?? 0) > 0 && (
              <Badge variant="secondary" className="ml-2">{readyForReleaseData?.totalCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="awaiting">
          <Card>
            <CardHeader><CardTitle className="text-base">Awaiting Acknowledgement</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {renderDispatchTable({
                records: awaitingAck,
                loading: loadingAck,
                showAck: true,
                pageNumber: awaitingAckData?.pageNumber ?? ackPage,
                pageSize: awaitingAckData?.pageSize ?? ackPageSize,
                totalCount: awaitingAckData?.totalCount ?? 0,
                totalPages: awaitingAckData?.totalPages ?? 0,
                isFetching: fetchingAck,
                onPageChange: setAckPage,
                onPageSizeChange: (size) => { setAckPageSize(size); setAckPage(1); },
              })}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="release">
          <Card>
            <CardHeader><CardTitle className="text-base">Ready for Release to Customer</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {renderDispatchTable({
                records: readyForRelease,
                loading: loadingRelease,
                showAck: false,
                pageNumber: readyForReleaseData?.pageNumber ?? releasePage,
                pageSize: readyForReleaseData?.pageSize ?? releasePageSize,
                totalCount: readyForReleaseData?.totalCount ?? 0,
                totalPages: readyForReleaseData?.totalPages ?? 0,
                isFetching: fetchingRelease,
                onPageChange: setReleasePage,
                onPageSizeChange: (size) => { setReleasePageSize(size); setReleasePage(1); },
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acknowledge Receipt — {selected?.dispatchNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Confirm you have received the dispatched items for <strong>{selected?.customerName}</strong>.
            </p>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any receipt remarks…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckOpen(false)}>Cancel</Button>
            <Button onClick={handleAcknowledge} disabled={acknowledgeReceipt.isPending}>
              {acknowledgeReceipt.isPending ? "Processing…" : "Acknowledge Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Dispatch History ───────────────────────────────────────────────────────

function DispatchHistoryPanel() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 400);
  const { data, isLoading, isFetching } = useDispatchHistoryPaged(page, pageSize, search || undefined);

  const records = data?.items ?? [];

  const handleSearch = (v: string) => {
    setSearchInput(v);
    setPage(1);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Dispatched Orders</CardTitle>
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search dispatch, order, customer…"
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : records.length === 0 ? (
          <p className="text-sm text-muted-foreground">{search ? "No results for that search." : "No dispatched records yet."}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dispatch No.</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Trays</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.dispatchNumber}</TableCell>
                  <TableCell className="font-mono text-xs">{r.orderNumber}</TableCell>
                  <TableCell>{r.customerName}</TableCell>
                  <TableCell>{r.fulfilmentMode}</TableCell>
                  <TableCell>{r.totalTraysDispatched}</TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(r.status)}>{statusLabel(r.status)}</Badge>
                  </TableCell>
                  <TableCell className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/dispatch/${r.id}`)}>
                      View
                    </Button>
                    <PermissionGate permissions={["dispatch.print_delivery_note"]}>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        title="Download Delivery Note"
                        onClick={() => dispatchApi.downloadDeliveryNotePdf(r.id)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {(data?.totalCount ?? 0) > 0 && (
          <TablePagination
            pageNumber={page}
            pageSize={pageSize}
            totalCount={data?.totalCount ?? 0}
            totalPages={data?.totalPages ?? 0}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            isFetching={isFetching}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Tray Returns History ──────────────────────────────────────────────────

function TrayReturnsHistoryPanel() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 400);
  const { data, isLoading, isFetching } = useTrayReturnsHistoryPaged(page, pageSize, search || undefined);

  const rows = data?.items ?? [];

  const handleSearch = (v: string) => {
    setSearchInput(v);
    setPage(1);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Tray Returns History</CardTitle>
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search dispatch, order, customer…"
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{search ? "No results for that search." : "No tray returns recorded yet."}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dispatch No.</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Return Date</TableHead>
                <TableHead className="text-right">Qty Returned</TableHead>
                <TableHead>Recorded By</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.dispatchNumber}</TableCell>
                  <TableCell className="font-mono text-xs">{r.orderNumber}</TableCell>
                  <TableCell>{r.customerName}</TableCell>
                  <TableCell>{formatDate(r.returnDate)}</TableCell>
                  <TableCell className="text-right font-semibold">{r.quantityReturned}</TableCell>
                  <TableCell>{r.recordedByUserName}</TableCell>
                  <TableCell className="text-muted-foreground text-xs max-w-[200px] truncate">{r.notes ?? "—"}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => navigate(`/dispatch/${r.dispatchRecordId}`)}
                    >
                      View Dispatch
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {(data?.totalCount ?? 0) > 0 && (
          <TablePagination
            pageNumber={page}
            pageSize={pageSize}
            totalCount={data?.totalCount ?? 0}
            totalPages={data?.totalPages ?? 0}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            isFetching={isFetching}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Outstanding Trays ──────────────────────────────────────────────────────

function OutstandingTraysPanel() {
  const [county, setCounty] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 400);
  const { data, isLoading, isFetching } = useOutstandingTraysPaged(page, pageSize, search || undefined, county);
  const [exporting, setExporting] = useState(false);
  const notify = useNotification();
  const navigate = useNavigate();

  const rows = data?.items ?? [];

  const handleSearch = (v: string) => {
    setSearchInput(v);
    setPage(1);
  };

  const handleCounty = (v: string) => {
    setCounty(v === "__all" ? undefined : v);
    setPage(1);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await dispatchApi.downloadOutstandingTraysPdf(county);
    } catch (err: unknown) {
      notify.error(normalizeError(err).userMessage || "Failed to export outstanding trays");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">Outstanding Trays by Customer</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search dispatch, order, customer…"
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-8 h-8 text-sm w-56"
              />
            </div>
            <Select value={county ?? "__all"} onValueChange={handleCounty}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All Counties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Counties</SelectItem>
                {KENYAN_COUNTIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || isLoading || (data?.totalCount ?? 0) === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              {exporting ? "Exporting…" : "Export PDF"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{search ? "No results for that search." : "No outstanding trays."}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dispatch No.</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>County</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="text-right">Dispatched</TableHead>
                <TableHead className="text-right">Returned</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.dispatchRecordId}>
                  <TableCell className="font-mono text-sm">{row.dispatchNumber}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell>{row.customerCounty}</TableCell>
                  <TableCell className="font-mono text-sm">{row.orderNumber}</TableCell>
                  <TableCell className="text-right">{row.totalTraysDispatched}</TableCell>
                  <TableCell className="text-right">{row.totalTraysReturned}</TableCell>
                  <TableCell className="text-right font-semibold text-destructive">{row.traysOutstanding}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => navigate(`/dispatch/${row.dispatchRecordId}`)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {(data?.totalCount ?? 0) > 0 && (
          <TablePagination
            pageNumber={page}
            pageSize={pageSize}
            totalCount={data?.totalCount ?? 0}
            totalPages={data?.totalPages ?? 0}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            isFetching={isFetching}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export const DispatchManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateOrderId, setInitiateOrderId] = useState<string | null>(null);
  const [assignmentPrefill, setAssignmentPrefill] = useState<DispatchAssignmentPrefill | null>(null);
  const { hasPermission } = useAuthStore();

  const canHandover = hasPermission("dispatch.confirm_handover");
  const canAcknowledge = hasPermission("dispatch.acknowledge_receipt");
  const canViewOutstanding = hasPermission("dispatch.view_outstanding_trays");
  const canViewCollections = hasPermission("dispatch.collections.view");

  const defaultTab = "ready";
  const availableTabs = [
    "ready",
    ...(canViewCollections ? ["collections"] : []),
    ...(canHandover ? ["supervisor"] : []),
    ...(canAcknowledge ? ["manager"] : []),
    ...(canViewOutstanding ? ["outstanding"] : []),
    "history",
    "tray-returns",
  ];
  const requestedTab = searchParams.get("tab");
  const activeTab = requestedTab && availableTabs.includes(requestedTab) ? requestedTab : defaultTab;

  const handleTopTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === defaultTab) {
      next.delete("tab");
    } else {
      next.set("tab", value);
    }
    if (value !== "manager") {
      next.delete("managerTab");
    }
    setSearchParams(next, { replace: true });
  };

  const handleExecutePublishedLine = (line: DispatchExecutionQueueLineDto) => {
    setInitiateOrderId(line.orderId);
    setAssignmentPrefill({
      deliveryPlanId: line.deliveryPlanId,
      driverName: line.driverName,
      vehicleName: line.vehicleRegistrationNumber,
      routeName: line.routeName,
    });
    setInitiateOpen(true);
  };

  useEffect(() => {
    const driverId = searchParams.get("driverId");
    const start = searchParams.get("start");
    const end = searchParams.get("end");

    if (!driverId || !start || !end) {
      return;
    }

    setAssignmentPrefill({
      deliveryPlanId: searchParams.get("deliveryPlanId") ?? undefined,
      driverId,
      driverName: searchParams.get("driverName") ?? undefined,
      routeId: searchParams.get("routeId") ?? undefined,
      routeName: searchParams.get("routeName") ?? undefined,
      vehicleId: searchParams.get("vehicleId") ?? undefined,
      vehicleName: searchParams.get("vehicleName") ?? undefined,
      greenhouseId: searchParams.get("greenhouseId") ?? undefined,
      greenhouseName: searchParams.get("greenhouseName") ?? undefined,
      start,
      end,
    });

    setInitiateOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dispatch</h1>
          <p className="text-sm text-muted-foreground">Order fulfilment workflow</p>
        </div>
        <div className="flex gap-2">
          <PermissionGate permissions={["dispatch.approve_compensation"]}>
            <Button variant="outline" onClick={() => navigate("/dispatch/compensation-notes")}>
              Compensation Notes
            </Button>
          </PermissionGate>
        </div>
      </div>

      <InitiateDispatchDialog
        open={initiateOpen}
        onOpenChange={(open) => {
          setInitiateOpen(open);
          if (!open) {
            setAssignmentPrefill(null);
            setInitiateOrderId(null);
          }
        }}
        onSuccess={() => {}}
        prefill={assignmentPrefill}
        preSelectedOrderId={initiateOrderId}
      />

      <Tabs value={activeTab} onValueChange={handleTopTabChange}>
        <TabsList>
          <TabsTrigger value="ready">Ready for Dispatch</TabsTrigger>
          {canViewCollections && <TabsTrigger value="collections">Customer Collection</TabsTrigger>}
          {canHandover && (
            <TabsTrigger value="supervisor">Supervisor Handover Queue</TabsTrigger>
          )}
          {canAcknowledge && (
            <TabsTrigger value="manager">Dispatch Manager Workspace</TabsTrigger>
          )}
          {canViewOutstanding && (
            <TabsTrigger value="outstanding">Outstanding Trays</TabsTrigger>
          )}
          <TabsTrigger value="history">Dispatch History</TabsTrigger>
          <TabsTrigger value="tray-returns">Tray Returns</TabsTrigger>
        </TabsList>

        <TabsContent value="ready" className="pt-2">
          <PublishedPlanExecutionPanel onExecute={handleExecutePublishedLine} />
        </TabsContent>
        {canViewCollections && <TabsContent value="collections" className="pt-2"><CustomerCollectionsPanel /></TabsContent>}
        {canHandover && (
          <TabsContent value="supervisor" className="pt-2">
            <GrowingSupervisorQueue />
          </TabsContent>
        )}
        {canAcknowledge && (
          <TabsContent value="manager" className="pt-2">
            <DispatchManagerWorkspace />
          </TabsContent>
        )}
        {canViewOutstanding && (
          <TabsContent value="outstanding" className="pt-2">
            <OutstandingTraysPanel />
          </TabsContent>
        )}
        <TabsContent value="history" className="pt-2">
          <DispatchHistoryPanel />
        </TabsContent>
        <TabsContent value="tray-returns" className="pt-2">
          <TrayReturnsHistoryPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};
